//#region header */
/**************************************************************************************************
//
//  Description: Form to display the application information
//
//  Copyright:    © 2025 Idox Software Limited
//
//--------------------------------------------------------------------------------------------------
//
//  Modification History:
//
//  Version Date     Modifier             Issue# Description
//#region Version 1.0.0.0 changes
//    001   25.02.25 Sean Flook          GMSCM-1 Initial Revision.
//#endregion Version 1.0.0.0 changes
//
//--------------------------------------------------------------------------------------------------
//#endregion header */

import React, { useEffect, useRef, useState } from "react";

import useUserQueryStore from "../context/UserStore";

import {
  ApplicationDbConnectionNames,
  ApplicationElasticConnectionNames,
  ApplicationNamesDataDto,
} from "../dtos/applicationNamesDto";
import { ApplicationErrorType } from "../models/applicationErrorType";

import { Box, Grid2, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import { DataGrid, GridCellParams, GridSlotsComponentsProps, GridSortModel } from "@mui/x-data-grid";
import ADSTextControl from "../components/ADSTextControl";
import ADSActionButton, { ADSActionButtonVariant } from "../components/ADSActionButton";
import ADSErrorDisplay from "../components/ADSErrorDisplay";

import ActionDialog, { ActionVariant, DialogMode } from "../dialogs/ActionDialog";

import AddIcon from "@mui/icons-material/Add";

import { adsBlueA, adsPaleBlueA, adsPaleBlueB, adsRed, adsRed10, adsRed20 } from "../utils/ADSColour";
import { ActionIconStyle, dataFormStyle, dataFormToolbarHeight, FormRowStyle, tooltipStyle } from "../utils/ADSStyles";
import { styled } from "@mui/material/styles";

declare module "@mui/x-data-grid" {
  interface FooterPropsOverrides {
    footerError: string | string[] | undefined;
  }
}

export function FooterErrorComponent(props: NonNullable<GridSlotsComponentsProps>["footer"]): React.ReactElement {
  return (
    <Box sx={{ p: 1, display: "flex" }}>
      <ADSErrorDisplay errorText={props?.footerError} id="data-grid-footer-error" removeLabelSpacer={true} />
    </Box>
  );
}

const StyledDataGrid = styled(DataGrid)(({ theme }) => ({
  "& .normal-row": {
    "&:hover": {
      backgroundColor: adsPaleBlueA,
      color: adsBlueA,
      cursor: "pointer",
    },
  },
  "& .selected-row": {
    backgroundColor: adsPaleBlueB,
    color: adsBlueA,
    boxShadow: `inset 4px 0px 0px 0px ${adsBlueA}`,
    "&:hover": {
      backgroundColor: adsPaleBlueA,
      color: adsBlueA,
      boxShadow: `inset 4px 0px 0px 0px ${adsBlueA}`,
      cursor: "pointer",
    },
  },
  "& .error-row": {
    backgroundColor: adsRed10,
    color: adsBlueA,
    boxShadow: `inset 4px 0px 0px 0px ${adsRed}`,
    "&:hover": {
      backgroundColor: adsRed20,
      color: adsBlueA,
      boxShadow: `inset 4px 0px 0px 0px ${adsRed}`,
      cursor: "pointer",
    },
  },
}));

interface ApplicationFormProps {
  data: ApplicationNamesDataDto;
  errors: ApplicationErrorType[] | undefined;
  onDataChange: (data: ApplicationNamesDataDto) => void;
}

const ApplicationForm: React.FC<ApplicationFormProps> = ({ data, errors, onDataChange }) => {
  const currentUser = useUserQueryStore((s) => s.userQuery.currentUser);

  const [name, setName] = useState<string>("");
  const [dbConnectionNames, setDbConnectionNames] = useState<ApplicationDbConnectionNames[]>([]);
  const [elasticConnectionNames, setElasticConnectionNames] = useState<ApplicationElasticConnectionNames[]>([]);

  const [nameError, setNameError] = useState<string[] | undefined>(undefined);
  const [dbConnectionNamesError, setDbConnectionNamesError] = useState<string[] | undefined>(undefined);
  const [elasticConnectionNamesError, setElasticConnectionNamesError] = useState<string[] | undefined>(undefined);
  const [namedConnectionSettingErrors, setNamedConnectionSettingErrors] = useState<string[] | undefined>(undefined);
  const [namedElasticSettingErrors, setNamedElasticSettingErrors] = useState<string[] | undefined>(undefined);

  const [connectionSortModel, setConnectionSortModel] = useState<GridSortModel>([]);
  const [selectedConnectionRow, setSelectedConnectionRow] = useState<number>(-1);

  const [elasticSortModel, setElasticSortModel] = useState<GridSortModel>([]);
  const [selectedElasticRow, setSelectedElasticRow] = useState<number>(-1);

  const [openActionDialog, setOpenActionDialog] = useState<boolean>(false);
  const actionData = useRef<ApplicationDbConnectionNames | ApplicationElasticConnectionNames | undefined>(undefined);
  const actionMode = useRef<DialogMode>(DialogMode.edit);
  const actionType = useRef<ActionVariant>(undefined);

  const displayConnectionActionButtons = (params: GridCellParams<ApplicationDbConnectionNames>): React.ReactElement => {
    const onEditClick = (event: React.MouseEvent<HTMLButtonElement>): void => {
      event.stopPropagation();
      actionMode.current = DialogMode.edit;
      actionType.current = ActionVariant.namedConnectionSetting;
      actionData.current = params.row;
      setOpenActionDialog(true);
    };

    const onDeleteClick = (event: React.MouseEvent<HTMLButtonElement>): void => {
      event.stopPropagation();
      actionMode.current = DialogMode.delete;
      actionType.current = ActionVariant.namedConnectionSetting;
      actionData.current = params.row;
      setOpenActionDialog(true);
    };

    return (
      <Stack direction="row" spacing={1} justifyContent="flex-start" alignItems="center">
        <ADSActionButton
          id={`edit-connection-${params.id}`}
          variant={ADSActionButtonVariant.edit}
          inheritBackground
          tooltipTitle={`Edit ${params.row.name}`}
          tooltipPlacement="bottom"
          onClick={onEditClick}
        />
        <ADSActionButton
          id={`delete-connection-${params.id}`}
          variant={ADSActionButtonVariant.delete}
          inheritBackground
          tooltipTitle={`Delete ${params.row.name}`}
          tooltipPlacement="bottom"
          onClick={onDeleteClick}
        />
      </Stack>
    );
  };

  const displayElasticActionButtons = (
    params: GridCellParams<ApplicationElasticConnectionNames>
  ): React.ReactElement => {
    const onEditClick = (event: React.MouseEvent<HTMLButtonElement>): void => {
      event.stopPropagation();
      actionMode.current = DialogMode.edit;
      actionType.current = ActionVariant.namedElasticSetting;
      actionData.current = params.row;
      setOpenActionDialog(true);
    };

    const onDeleteClick = (event: React.MouseEvent<HTMLButtonElement>): void => {
      event.stopPropagation();
      actionMode.current = DialogMode.delete;
      actionType.current = ActionVariant.namedElasticSetting;
      actionData.current = params.row;
      setOpenActionDialog(true);
    };

    return selectedElasticRow && params.id === selectedElasticRow ? (
      <Stack direction="row" spacing={1} justifyContent="flex-start" alignItems="center">
        <ADSActionButton
          id={`edit-elastic-${params.id}`}
          variant={ADSActionButtonVariant.edit}
          inheritBackground
          tooltipTitle={`Edit ${params.row.name}`}
          tooltipPlacement="bottom"
          onClick={onEditClick}
        />
        <ADSActionButton
          id={`delete-elastic-${params.id}`}
          variant={ADSActionButtonVariant.delete}
          inheritBackground
          tooltipTitle={`Delete ${params.row.name}`}
          tooltipPlacement="bottom"
          onClick={onDeleteClick}
        />
      </Stack>
    ) : (
      <></>
    );
  };

  const connectionColumns = [
    {
      field: "id",
      headerClassName: "idox-data-grid-header",
      hide: true,
      sortable: false,
      filterable: false,
    },
    {
      field: "name",
      headerName: "Connection setting name",
      headerClassName: "idox-data-grid-header",
      flex: 10,
      sortable: true,
      filterable: false,
      sortComparator: (v1: string, v2: string) =>
        v1.localeCompare(v2, undefined, { numeric: true, sensitivity: "base" }),
    },
    {
      field: "actions",
      type: "actions",
      headerClassName: "idox-data-grid-header",
      sortable: false,
      filterable: false,
      flex: 2,
      headerName: "",
      getActions: (params: GridCellParams<ApplicationDbConnectionNames>) => {
        if (selectedConnectionRow !== -1 && params.id === selectedConnectionRow) {
          const actionButtons = displayConnectionActionButtons(params);
          return React.isValidElement(actionButtons) && (actionButtons.props as any).children
            ? Array.isArray((actionButtons.props as any).children)
              ? (actionButtons.props as any).children
              : [(actionButtons.props as any).children]
            : [];
        } else {
          return [];
        }
      },
    },
  ] as const;

  const initialConnectionState = {
    columns: {
      columnVisibilityModel: {
        id: false,
      },
    },
  };

  const elasticColumns = [
    {
      field: "id",
      headerClassName: "idox-data-grid-header",
      hide: true,
      sortable: false,
      filterable: false,
    },
    {
      field: "name",
      headerName: "Elastic setting name",
      headerClassName: "idox-data-grid-header",
      flex: 10,
      sortable: false,
      filterable: false,
      sortComparator: (v1: string, v2: string) =>
        v1.localeCompare(v2, undefined, { numeric: true, sensitivity: "base" }),
    },
    {
      field: "actions",
      type: "actions",
      headerClassName: "idox-data-grid-header",
      sortable: false,
      filterable: false,
      flex: 2,
      headerName: "",
      getActions: (params: GridCellParams<ApplicationElasticConnectionNames>) => {
        if (selectedElasticRow !== -1 && params.id === selectedElasticRow) {
          const actionButtons = displayElasticActionButtons(params);
          return React.isValidElement(actionButtons) && (actionButtons.props as any).children
            ? Array.isArray((actionButtons.props as any).children)
              ? (actionButtons.props as any).children
              : [(actionButtons.props as any).children]
            : [];
        } else {
          return [];
        }
      },
    },
  ] as const;

  const initialElasticState = {
    columns: {
      columnVisibilityModel: {
        id: false,
      },
    },
  };

  /**
   * Event to handle when the mouse enters a row in the connection data grid.
   *
   * @param {object} event The event object.
   */
  const handleConnectionRowMouseEnter = (event: React.MouseEvent<HTMLDivElement>) => {
    event.preventDefault();
    updateConnectionRowId(Number(event.currentTarget.getAttribute("data-id")));
  };

  /**
   * Event to handle when the mouse leaves a row in the connection data grid.
   */
  const handleConnectionRowMouseLeave = () => {
    updateConnectionRowId(-1);
  };

  /**
   * Event to handle when the mouse enters a row in the elastic data grid.
   *
   * @param {object} event The event object.
   */
  const handleElasticRowMouseEnter = (event: React.MouseEvent<HTMLDivElement>) => {
    event.preventDefault();
    updateElasticRowId(Number(event.currentTarget.getAttribute("data-id")));
  };

  /**
   * Event to handle when the mouse leaves a row in the elastic data grid.
   */
  const handleElasticRowMouseLeave = () => {
    updateElasticRowId(-1);
  };

  /**
   * Method to update the data.
   * @param fieldName The field name to update.
   * @param value The new value.
   */
  const updateData = (fieldName: string, value: string | string[] | undefined): void => {
    const updatedApplication = {
      ...data,
      [fieldName]: value,
    };

    if (onDataChange) {
      onDataChange(updatedApplication);
    }
  };

  /**
   * Event to handle when the name is changed.
   * @param newValue The new name.
   */
  const handleNameChangeEvent = (newValue: string): void => {
    setName(newValue);
    updateData("name", newValue);
  };

  /**
   * Method to set the various variables to the new row id.
   *
   * @param {Number} rowId The id of the selected row in the grid
   */
  const updateConnectionRowId = (rowId: number) => {
    setSelectedConnectionRow(rowId);
  };

  /**
   * Method to set the various variables to the new row id.
   *
   * @param {Number} rowId The id of the selected row in the grid
   */
  const updateElasticRowId = (rowId: number) => {
    setSelectedElasticRow(rowId);
  };

  /**
   * Method to handle adding a new connection setting.
   */
  const handleAddNewConnection = (): void => {
    actionData.current = {
      id: dbConnectionNames.length > 0 ? dbConnectionNames[dbConnectionNames.length - 1].id + 1 : 0,
      name: "",
    };
    actionMode.current = DialogMode.add;
    actionType.current = ActionVariant.namedConnectionSetting;
    setOpenActionDialog(true);
  };

  /**
   * Method to handle adding a new elastic setting.
   */
  const handleAddNewElastic = (): void => {
    actionData.current = {
      id: elasticConnectionNames.length > 0 ? elasticConnectionNames[elasticConnectionNames.length - 1].id + 1 : 0,
      name: "",
    };
    actionMode.current = DialogMode.add;
    actionType.current = ActionVariant.namedElasticSetting;
    setOpenActionDialog(true);
  };

  /**
   * Method to handle the edit named setting done event.
   *
   * @param {ApplicationDbConnectionNames | ApplicationElasticConnectionNames} data The new setting name data.
   */
  const handleActionDialogDone = (data: ApplicationDbConnectionNames | ApplicationElasticConnectionNames): void => {
    switch (actionType.current) {
      case ActionVariant.namedConnectionSetting:
        if (actionMode.current === DialogMode.add) {
          const updatedDbConnectionNames = dbConnectionNames.map((x) => x.name);
          updatedDbConnectionNames.push(data.name);
          updateData("dbConnectionNames", updatedDbConnectionNames);
          setDbConnectionNames((prevState) => [...prevState, { id: data.id, name: data.name }]);
        } else if (actionMode.current === DialogMode.edit) {
          const updatedDbConnectionNames = dbConnectionNames.map((x) =>
            x.id === data.id ? { id: x.id, name: data.name } : x
          );
          updateData(
            "dbConnectionNames",
            updatedDbConnectionNames.map((x) => x.name)
          );
          setDbConnectionNames(updatedDbConnectionNames);
        } else if (actionMode.current === DialogMode.delete) {
          const updatedDbConnectionNames = dbConnectionNames.filter((x) => x.id !== data.id);
          updateData(
            "dbConnectionNames",
            updatedDbConnectionNames.map((x) => x.name)
          );
          setDbConnectionNames(updatedDbConnectionNames);
        }
        break;

      case ActionVariant.namedElasticSetting:
        if (actionMode.current === DialogMode.add) {
          const updatedElasticConnectionNames = elasticConnectionNames.map((x) => x.name);
          updatedElasticConnectionNames.push(data.name);
          updateData("elasticConnectionNames", updatedElasticConnectionNames);
          setElasticConnectionNames((prevState) => [...prevState, { id: data.id, name: data.name }]);
        } else if (actionMode.current === DialogMode.edit) {
          const updatedElasticConnectionNames = elasticConnectionNames.map((x) =>
            x.id === data.id ? { id: x.id, name: data.name } : x
          );
          updateData(
            "elasticConnectionNames",
            updatedElasticConnectionNames.map((x) => x.name)
          );
          setElasticConnectionNames(updatedElasticConnectionNames);
        } else if (actionMode.current === DialogMode.delete) {
          const updatedElasticConnectionNames = elasticConnectionNames.filter((x) => x.id !== data.id);
          updateData(
            "elasticConnectionNames",
            updatedElasticConnectionNames.map((x) => x.name)
          );
          setElasticConnectionNames(updatedElasticConnectionNames);
        }
        break;

      default:
        break;
    }

    setOpenActionDialog(false);
  };

  /**
   * Method to handle the edit named setting close event.
   */
  const handleActionDialogClose = (): void => {
    setOpenActionDialog(false);
  };

  /**
   * Method to handle the delete named setting event.
   */
  const handleActionDialogDelete = (): void => {
    switch (actionType.current) {
      case ActionVariant.namedConnectionSetting:
        if (actionData.current) {
          const updatedDbConnectionNames = dbConnectionNames.filter(
            (x) => x.id !== (actionData.current as ApplicationDbConnectionNames).id
          );
          updateData(
            "dbConnectionNames",
            updatedDbConnectionNames.map((x) => x.name)
          );
          setDbConnectionNames(updatedDbConnectionNames);
        }
        break;

      case ActionVariant.namedElasticSetting:
        if (actionData.current) {
          const updatedElasticConnectionNames = elasticConnectionNames.filter(
            (x) => x.id !== (actionData.current as ApplicationElasticConnectionNames).id
          );
          updateData(
            "elasticConnectionNames",
            updatedElasticConnectionNames.map((x) => x.name)
          );
          setElasticConnectionNames(updatedElasticConnectionNames);
        }
        break;

      default:
        break;
    }
    setOpenActionDialog(false);
  };

  useEffect(() => {
    if (data) {
      setName(data.name ?? "");
      setDbConnectionNames((data.dbConnectionNames ?? []).map((item, idx) => ({ id: idx, name: item })));
      setElasticConnectionNames((data.elasticConnectionNames ?? []).map((item, idx) => ({ id: idx, name: item })));
    }
  }, [data]);

  useEffect(() => {
    setNameError(undefined);
    setDbConnectionNamesError(undefined);
    setElasticConnectionNamesError(undefined);

    if (errors && errors.length > 0) {
      for (const error of errors.filter((e) => e.id === data.id)) {
        if (error.field.startsWith("dbConnectionNames|")) {
          const settingName = error.field.split("|")[1];
          setDbConnectionNamesError((prevState) => (prevState ? [...prevState, error.error] : [error.error]));
          setNamedConnectionSettingErrors((prevState) => (prevState ? [...prevState, settingName] : [settingName]));
        } else if (error.field.startsWith("elasticConnectionNames|")) {
          const settingName = error.field.split("|")[1];
          setElasticConnectionNamesError((prevState) => (prevState ? [...prevState, error.error] : [error.error]));
          setNamedElasticSettingErrors((prevState) => (prevState ? [...prevState, settingName] : [settingName]));
        } else {
          switch (error.field) {
            case "name":
              setNameError((prevState) => (prevState ? [...prevState, error.error] : [error.error]));
              break;

            case "dbConnectionNames":
              setDbConnectionNamesError((prevState) => (prevState ? [...prevState, error.error] : [error.error]));
              break;

            case "elasticConnectionNames":
              setElasticConnectionNamesError((prevState) => (prevState ? [...prevState, error.error] : [error.error]));
              break;

            default:
              break;
          }
        }
      }
    }
  }, [data.id, errors]);

  return (
    <>
      <Box sx={dataFormStyle("ApplicationForm")}>
        <Grid2 container justifyContent="flex-start" alignItems="baseline" sx={FormRowStyle()}>
          <Grid2 size={12}>
            <ADSTextControl
              label="Name"
              value={name}
              maxLength={100}
              id="application_name"
              isEditable={currentUser?.isSuperAdministrator}
              isRequired
              helperText="The name of the application."
              errorText={nameError}
              onChange={handleNameChangeEvent}
            />
            <Stack spacing={2} sx={{ marginTop: 4 }} direction={"row"}>
              <Stack spacing={1} sx={{ flex: 1 }} direction={"column"}>
                <Stack
                  spacing={2}
                  sx={{ pl: "12px", pr: "4px", justifyContent: "space-between", alignItems: "center" }}
                  direction={"row"}
                >
                  <Typography variant="subtitle1">Named connection settings</Typography>
                  <Tooltip title={"Add named connection setting"} arrow placement="right" sx={tooltipStyle}>
                    <IconButton
                      onClick={handleAddNewConnection}
                      sx={ActionIconStyle()}
                      aria-controls="add-connection"
                      size="small"
                    >
                      <AddIcon />
                    </IconButton>
                  </Tooltip>
                </Stack>
                <Box sx={dataFormStyle("ApplicationDataGridBox")}>
                  <StyledDataGrid
                    rows={dbConnectionNames}
                    columns={connectionColumns}
                    initialState={initialConnectionState}
                    autoPageSize
                    columnHeaderHeight={Number(dataFormToolbarHeight)}
                    disableColumnMenu
                    disableColumnSelector
                    disableDensitySelector
                    hideFooterSelectedRowCount
                    editMode="row"
                    pagination
                    sortModel={connectionSortModel}
                    slots={{ footer: FooterErrorComponent }}
                    slotProps={{
                      row: {
                        onMouseEnter: handleConnectionRowMouseEnter,
                        onMouseLeave: handleConnectionRowMouseLeave,
                      },
                      footer: { footerError: dbConnectionNamesError },
                    }}
                    getRowClassName={(params) =>
                      `${
                        params.id === selectedConnectionRow
                          ? namedConnectionSettingErrors?.includes(params.row.name)
                            ? "error-row"
                            : "selected-row"
                          : "normal-row"
                      }`
                    }
                    onSortModelChange={(model) => setConnectionSortModel(model)}
                  />
                </Box>
              </Stack>
              <Stack spacing={1} sx={{ flex: 1 }} direction={"column"}>
                <Stack
                  spacing={2}
                  sx={{ pl: "12px", pr: "4px", justifyContent: "space-between", alignItems: "center" }}
                  direction={"row"}
                >
                  <Typography variant="subtitle1">Named elastic settings</Typography>
                  <Tooltip title={"Add named elastic setting"} arrow placement="right" sx={tooltipStyle}>
                    <IconButton
                      onClick={handleAddNewElastic}
                      sx={ActionIconStyle()}
                      aria-controls="add-elastic"
                      size="small"
                    >
                      <AddIcon />
                    </IconButton>
                  </Tooltip>
                </Stack>
                <Box sx={dataFormStyle("ApplicationDataGridBox")}>
                  <StyledDataGrid
                    rows={elasticConnectionNames}
                    columns={elasticColumns}
                    initialState={initialElasticState}
                    autoPageSize
                    columnHeaderHeight={Number(dataFormToolbarHeight)}
                    disableColumnMenu
                    disableColumnSelector
                    disableDensitySelector
                    hideFooterSelectedRowCount
                    editMode="row"
                    pagination
                    sortModel={elasticSortModel}
                    slots={{ footer: FooterErrorComponent }}
                    slotProps={{
                      row: {
                        onMouseEnter: handleElasticRowMouseEnter,
                        onMouseLeave: handleElasticRowMouseLeave,
                      },
                      footer: { footerError: elasticConnectionNamesError },
                    }}
                    getRowClassName={(params) =>
                      `${
                        params.id === selectedElasticRow
                          ? namedElasticSettingErrors?.includes(params.row.name)
                            ? "error-row"
                            : "selected-row"
                          : "normal-row"
                      }`
                    }
                    onSortModelChange={(model) => setElasticSortModel(model)}
                  />
                </Box>
              </Stack>
            </Stack>
          </Grid2>
        </Grid2>
      </Box>
      <ActionDialog
        open={openActionDialog}
        mode={actionMode.current!}
        variant={actionType.current!}
        data={actionData.current}
        onDone={handleActionDialogDone}
        onClose={handleActionDialogClose}
        onDelete={handleActionDialogDelete}
      />
    </>
  );
};

export default ApplicationForm;
